import { POST, GET, PATCH } from './executer';
import apiServiceInstance from './apiService';

export type FeedbackType = 'bug' | 'feature';
export type FeedbackStatus = 'open' | 'reviewed' | 'planned' | 'closed';

export interface CloseFeedbackRequest {
  sendEmail: boolean;
  response?: string;
}

export interface CloseFeedbackResponse {
  success: boolean;
  message: string;
  emailSent: boolean;
  emailError?: string;
}

export interface CreateFeedbackRequest {
  type: FeedbackType;
  title: string;
  description: string;
  screenshot?: string; // Base64 encoded image
  page_url?: string;
  user_agent?: string;
}

export interface Feedback {
  id: string;
  type: FeedbackType;
  title: string;
  description: string;
  status: FeedbackStatus;
  screenshot_url: string | null;
  page_url: string | null;
  user_agent: string | null;
  user_id: string;
  user_email: string;
  user_name: string;
  team: string | null;
  response: string | null;
  response_by: string | null;
  response_at: string | null;
  created_at: string;
  updated_at: string;
}

class FeedbackService {
  /**
   * Create a new feedback entry (bug report or feature request)
   */
  async createFeedback(data: CreateFeedbackRequest) {
    return await apiServiceInstance.client.execute<Feedback>(
      POST,
      '/api/feedback',
      data
    );
  }

  /**
   * Get current user's feedback entries
   */
  async getMyFeedback() {
    return await apiServiceInstance.client.execute<Feedback[]>(
      GET,
      '/api/feedback/my'
    );
  }

  /**
   * Get all feedback entries from the current user's team
   */
  async getTeamFeedback() {
    return await apiServiceInstance.client.execute<{ data: Feedback[]; count: number; team: string }>(
      GET,
      '/api/feedback/team'
    );
  }

  /**
   * Get all feedback entries (admin only)
   */
  async getAllFeedback() {
    return await apiServiceInstance.client.execute<Feedback[]>(
      GET,
      '/api/feedback'
    );
  }

  /**
   * Update feedback status (admin only)
   */
  async updateFeedbackStatus(
    feedbackId: string, 
    status: FeedbackStatus, 
    options?: { sendEmail?: boolean; message?: string }
  ) {
    return await apiServiceInstance.client.execute<{ success: boolean; message: string; emailSent?: boolean }>(
      PATCH,
      `/api/feedback/${feedbackId}/status`,
      { status, ...options }
    );
  }

  /**
   * Update feedback response (admin only)
   */
  async updateFeedbackResponse(feedbackId: string, response: string) {
    return await apiServiceInstance.client.execute<void>(
      PATCH,
      `/api/feedback/${feedbackId}/response`,
      { response }
    );
  }

  /**
   * Close feedback with optional email notification to submitter (admin only)
   */
  async closeFeedback(feedbackId: string, data: CloseFeedbackRequest) {
    return await apiServiceInstance.client.execute<CloseFeedbackResponse>(
      POST,
      `/api/feedback/${feedbackId}/close`,
      data
    );
  }

  /**
   * Update feedback description (owner only, open items only)
   */
  async updateFeedbackDescription(feedbackId: string, description: string) {
    return await apiServiceInstance.client.execute<void>(
      PATCH,
      `/api/feedback/${feedbackId}/description`,
      { description }
    );
  }
}

const feedbackServiceInstance = new FeedbackService();
export default feedbackServiceInstance;

